/**
 * El marco de la aplicación: menú lateral y barra superior.
 *
 * Sigue el mismo patrón que NARA CRM (`/root/nara-crm/frontend/src/
 * componentes/Marco.jsx`): menú azul oscuro con el nombre de la aplicación,
 * barra blanca arriba con el título de la pantalla, el rol, el usuario y
 * salir. Las aplicaciones de NARA se tienen que sentir como una sola.
 *
 * Lo propio del ERP vive en la barra superior: la sede y el período, que se
 * escogen UNA vez y mandan en todas las pantallas. Antes se pedían en cada
 * pantalla, con el riesgo de facturar por la sede equivocada o de mirar el
 * estado de resultados de julio con los egresos de septiembre.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar, Avatar, Box, Chip, Divider, Drawer, IconButton, List, ListItemButton,
  ListItemIcon, ListItemText, MenuItem, Select, TextField, Toolbar, Tooltip, Typography,
  useMediaQuery,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaymentsIcon from '@mui/icons-material/Payments';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import BuildIcon from '@mui/icons-material/Build';
import StoreIcon from '@mui/icons-material/Store';
import CategoryIcon from '@mui/icons-material/Category';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import TuneIcon from '@mui/icons-material/Tune';
import BadgeIcon from '@mui/icons-material/Badge';
import GroupIcon from '@mui/icons-material/Group';
import HistoryIcon from '@mui/icons-material/History';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import AppsIcon from '@mui/icons-material/Apps';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import api from '../api';
import { useSede } from '../contexto/Sede';
import { empresaActual, listaEmpresas } from '../empresa';
import SelectorEmpresa from './SelectorEmpresa';
import { usePeriodo, periodoActual } from '../contexto/Periodo';
import { HUB, irAlHub } from '../hub';
import { abrirEnPestana } from '../pestana';

const ANCHO = 232;
const isTestEnv = process.env.REACT_APP_ENV === 'test';
const TEST_BANNER_HEIGHT = isTestEnv ? 32 : 0;

const MENU = [
  { texto: 'Tablero', icono: <DashboardIcon />, ruta: '/' },
  { texto: 'Terceros', icono: <PeopleIcon />, ruta: '/terceros' },
  // Solo si la empresa tiene algún tipo de orden activo: una que solo
  // factura no usa órdenes.
  { texto: 'Órdenes', icono: <BuildIcon />, ruta: '/ordenes', conOrdenes: true },
  { texto: 'Productos y servicios', icono: <Inventory2Icon />, ruta: '/servicios' },
  { texto: 'Facturas', icono: <ReceiptIcon />, ruta: '/facturas' },
  { texto: 'Pagos', icono: <PaymentsIcon />, ruta: '/pagos' },
  { texto: 'Cartera', icono: <AccountBalanceWalletIcon />, ruta: '/cartera' },
  { texto: 'Egresos', icono: <TrendingDownIcon />, ruta: '/egresos' },
  { seccion: 'Contabilidad' },
  { texto: 'Plan de cuentas', icono: <AccountTreeIcon />, ruta: '/contabilidad/cuentas' },
  { texto: 'Libro diario', icono: <MenuBookIcon />, ruta: '/contabilidad/diario' },
  { texto: 'Reportes', icono: <AssessmentIcon />, ruta: '/contabilidad/reportes' },
  { seccion: 'Sistema' },
  { texto: 'Sedes', icono: <StoreIcon />, ruta: '/sedes' },
  { texto: 'Conceptos', icono: <CategoryIcon />, ruta: '/conceptos' },
  { texto: 'Tipos de orden', icono: <TuneIcon />, ruta: '/tipos-orden', soloAdmin: true },
  { texto: 'Empleados', icono: <BadgeIcon />, ruta: '/maestros' },
  { texto: 'Usuarios', icono: <GroupIcon />, ruta: '/usuarios', soloAdmin: true },
  { texto: 'Auditoría', icono: <HistoryIcon />, ruta: '/auditoria', soloAdmin: true },
];
const CONFIGURACION = { texto: 'Configuración', icono: <SettingsIcon />, ruta: '/configuracion' };

const ROL = { admin: 'Administrador', contador: 'Contador', solo_lectura: 'Solo lectura' };

const activa = (ruta, pathname) =>
  pathname === ruta || (ruta !== '/' && pathname.startsWith(ruta + '/'));

// Los selectores de la barra blanca: compactos y del mismo alto.
const SELECTOR = {
  fontSize: 13, fontWeight: 600, bgcolor: '#f8fafc',
  '& .MuiOutlinedInput-input, & .MuiSelect-select': { py: 0.6 },
};

export default function Layout({ children }) {
  const navigate = useNavigate();
  const { sedes, sedeId, setSedeId, unaSola, nombre: nombreSede } = useSede();
  const { periodo, setPeriodo, esActual } = usePeriodo();
  const location = useLocation();
  const estrecho = useMediaQuery('(max-width:900px)');
  const [abierto, setAbierto] = useState(false);
  // El rol decide qué entradas del menú se ven. Mostrar «Usuarios» a quien no
  // puede abrirlo solo produce un 403 y la sensación de que algo se dañó.
  const [yo, setYo] = useState(null);
  useEffect(() => {
    api.get('/auth/me').then(({ data }) => setYo(data)).catch(() => {});
  }, []);
  const rol = yo?.rol;
  // Se vuelve a preguntar al cambiar de pantalla: así, al crear el primer tipo
  // de orden, «Órdenes» aparece en el menú sin recargar.
  const [usaOrdenes, setUsaOrdenes] = useState(false);
  useEffect(() => {
    api.get('/tipos-orden').then(({ data }) => setUsaOrdenes((data || []).length > 0)).catch(() => {});
  }, [location.pathname]);

  const navegar = (ruta) => { navigate(ruta); if (estrecho) setAbierto(false); };
  const visibles = MENU.filter((m) => (!m.soloAdmin || rol === 'admin') && (!m.conOrdenes || usaOrdenes));
  const titulo = [...MENU, CONFIGURACION].find((m) => m.ruta && activa(m.ruta, location.pathname))?.texto || '';
  const nombre = yo?.nombre || localStorage.getItem('user_nombre') || '';
  const correo = yo?.email || localStorage.getItem('user_email') || '';

  // Salir cierra el ERP y vuelve al hub sin cerrar su sesión (2026-10-30,
  // Raúl): de todo se sale en el hub.
  const salir = async () => {
    await signOut(auth);
    localStorage.clear();
    irAlHub();
  };

  const item = (m) => {
    const sel = activa(m.ruta, location.pathname);
    return (
      <ListItemButton key={m.ruta} onClick={() => navegar(m.ruta)}
        sx={{
          mx: 1, my: 0.25, borderRadius: 2, py: 0.9,
          bgcolor: sel ? 'rgba(255,255,255,0.14)' : 'transparent',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
        }}>
        <ListItemIcon sx={{ minWidth: 36, color: sel ? '#fff' : '#94a3b8' }}>{m.icono}</ListItemIcon>
        <ListItemText primaryTypographyProps={{
          fontSize: 13.5, fontWeight: sel ? 700 : 500, color: sel ? '#fff' : '#cbd5e1',
        }} primary={m.texto} />
      </ListItemButton>
    );
  };

  const menu = (
    <Box sx={{ width: ANCHO, height: '100%', bgcolor: '#1e3a5f', color: '#e2e8f0',
               display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2.5, pb: 2 }}>
        <Typography sx={{ fontSize: 20, fontWeight: 800, letterSpacing: -0.4 }}>
          NARA ERP
        </Typography>
        <SelectorEmpresa empresas={listaEmpresas() || []} actual={empresaActual()}
                         respaldo="Facturación y contabilidad" />
      </Box>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)' }} />
      <List sx={{ flexGrow: 1, py: 1, overflowY: 'auto' }}>
        {visibles.map((m, i) => (m.seccion ? (
          <Typography key={`s-${i}`} sx={{ px: 2.5, pt: 1.75, pb: 0.5, fontSize: 10.5,
            fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.8 }}>
            {m.seccion}
          </Typography>
        ) : item(m)))}
      </List>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)' }} />
      <Box sx={{ py: 1 }}>{item(CONFIGURACION)}</Box>
    </Box>
  );

  // Sede y período: en la barra, visibles siempre (también en el teléfono,
  // donde el menú está cerrado y si no se ven se factura a ciegas).
  const sede = sedes.length > 0 && (unaSola ? (
    <Chip size="small" label={nombreSede} sx={{ bgcolor: '#f1f5f9', fontSize: 11.5 }} />
  ) : (
    <Select size="small" value={sedeId === null ? 'todas' : sedeId}
      onChange={(e) => setSedeId(e.target.value)} sx={{ ...SELECTOR, maxWidth: { xs: 150, sm: 210 } }}>
      <MenuItem value="todas">Todas las sedes</MenuItem>
      {sedes.map((s) => <MenuItem key={s.id} value={s.id}>{s.nombre}</MenuItem>)}
    </Select>
  ));

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', pt: `${TEST_BANNER_HEIGHT}px` }}>
      {estrecho ? (
        <Drawer open={abierto} onClose={() => setAbierto(false)}>{menu}</Drawer>
      ) : (
        <Box sx={{ width: ANCHO, flexShrink: 0 }}>
          <Box sx={{ position: 'fixed', width: ANCHO, top: TEST_BANNER_HEIGHT, bottom: 0 }}>{menu}</Box>
        </Box>
      )}

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <AppBar position="sticky" elevation={0}
                sx={{ top: TEST_BANNER_HEIGHT, bgcolor: '#fff', color: '#0f172a',
                      borderBottom: '1px solid #e2e8f0' }}>
          <Toolbar sx={{ gap: 1, minHeight: 58, flexWrap: { xs: 'wrap', md: 'nowrap' },
                         py: { xs: 0.75, md: 0 } }}>
            {estrecho && (
              <IconButton edge="start" onClick={() => setAbierto(true)}><MenuIcon /></IconButton>
            )}
            <Typography noWrap sx={{ fontSize: 15, fontWeight: 700, flexGrow: 1, minWidth: 0 }}>
              {titulo}
            </Typography>
            {/* En el escritorio, sede y período van antes del usuario; en el
                teléfono bajan a su propia línea para no apretar el título. */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center',
                       order: { xs: 10, md: 0 }, width: { xs: '100%', md: 'auto' },
                       pb: { xs: 0.25, md: 0 } }}>
              {sede}
              <TextField type="month" size="small" value={periodo}
                onChange={(e) => setPeriodo(e.target.value)}
                sx={{ ...SELECTOR, width: 160,
                      ...(esActual ? {} : { bgcolor: '#fef3c7',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#f59e0b' } }) }} />
              {!esActual && (
                <Chip size="small" label="Volver al mes actual" onClick={() => setPeriodo(periodoActual())}
                  sx={{ bgcolor: '#fef3c7', color: '#92400e', fontSize: 11 }} />
              )}
            </Box>
            {!estrecho && rol && (
              <Chip size="small" label={ROL[rol] || rol}
                    sx={{ bgcolor: '#e0f2fe', color: '#0369a1', fontSize: 11, ml: 1 }} />
            )}
            <Tooltip title={correo}>
              <Avatar sx={{ width: 32, height: 32, fontSize: 13, bgcolor: '#1e3a5f' }}>
                {(nombre || correo || '?').slice(0, 1).toUpperCase()}
              </Avatar>
            </Tooltip>
            <Tooltip title="Aplicaciones de NARA">
              {/* Vuelve a la pestaña del hub sin recargarla; si no hay,
                  la abre. */}
              <IconButton onClick={() => abrirEnPestana(HUB, 'nara-apps')}><AppsIcon /></IconButton>
            </Tooltip>
            <Tooltip title="Salir">
              <IconButton onClick={salir}><LogoutIcon /></IconButton>
            </Tooltip>
          </Toolbar>
        </AppBar>
        <Box component="main" sx={{
          // Sin minWidth 0, un hijo ancho (una tabla) estira todo el documento
          // y aparece el scroll horizontal de la página.
          minWidth: 0, maxWidth: '100%', p: { xs: 1.5, md: 3 },
        }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
}
